import { Injectable } from '@angular/core';
import {
    CreatePremiumOrderInput,
    ModelPremiumOrderFilterInput,
    ModelSortDirection,
    PremiumOrder,
    UpdatePremiumOrderInput
} from '@app/core/services/api/API.service';
import { generateClient } from 'aws-amplify/api';
import * as queries from '@graphql/queries';
import * as mutations from '@graphql/mutations';
import { Observable, catchError, finalize, from, tap, throwError } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class PremiumOrderService {
    private amplifyClient = generateClient();

    constructor() {}

    async listPremiumOrders(): Promise<PremiumOrder[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.listPremiumOrders
                })
            ).data.listPremiumOrders.items;
            if (data) return data as PremiumOrder[];
        } catch (error) {
            console.error('Error finding PremiumOrder:', error);
            return null;
        }
    }
    async premiumOrdersByBuyer(
        orderCreatorId: string
    ): Promise<PremiumOrder[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.PremiumOrdersByBuyer,
                    variables: {
                        orderCreatorId
                    }
                })
            ).data.PremiumOrdersByBuyer.items;
            if (data) return data as PremiumOrder[];
        } catch (error) {
            console.error('Error finding PremiumOrder:', error);
            return null;
        }
    }

    async premiumOrdersByClient(
        premiumOrderClientId: string,
        filter?: ModelPremiumOrderFilterInput | null
    ): Promise<PremiumOrder[]> {
        let data = [];
        let response = null;
        try {
            do {
                response = (
                    await this.amplifyClient.graphql({
                        query: queries.PremiumOrdersByClient,
                        variables: {
                            premiumOrderClientId,
                            filter,
                            nextToken: response?.nextToken ?? null
                        }
                    })
                ).data.PremiumOrdersByClient;
                if (response && response.items) {
                    data.push(...(response.items as PremiumOrder[]));
                }
            } while (response.nextToken !== null);
            if (data) return data as PremiumOrder[];
        } catch (error) {
            console.error('Error finding PremiumOrders:', error);
            return null;
        }
    }

    async premiumOrdersByOrderId(
        active = 'active',
        limit = 1,
        sortDirection = ModelSortDirection.DESC
    ) {
        try {
            const response = (
                await this.amplifyClient.graphql({
                    query: queries.PremiumOrdersByOrderId,
                    variables: {
                        active,
                        sortDirection,
                        limit
                    }
                })
            ).data.PremiumOrdersByOrderId.items;
            if (response) return response;
        } catch (error) {
            console.error('Error updating premium order:', error);
            return null;
        }
    }

    async updatePremiumOrder(updateInput: UpdatePremiumOrderInput) {
        try {
            const response = (
                await this.amplifyClient.graphql({
                    query: mutations.updatePremiumOrder,
                    variables: {
                        input: updateInput
                    }
                })
            ).data.updatePremiumOrder;
            if (response) return response;
        } catch (error) {
            console.error('Error updating premium order:', error);
            return null;
        }
    }

    async updatePremiumOrderListsProducts({
        id,
        listsProducts
    }: UpdatePremiumOrderInput) {
        try {
            const response = (
                await this.amplifyClient.graphql({
                    query: mutations.updatePremiumOrder,
                    variables: {
                        input: {
                            id,
                            listsProducts
                        }
                    }
                })
            ).data.updatePremiumOrder;
            if (response) return response;
        } catch (error) {
            console.error('Error updating premium order:', error);
            return null;
        }
    }

    async updatePremiumOrderStatus({ id, active }: UpdatePremiumOrderInput) {
        try {
            const response = (
                await this.amplifyClient.graphql({
                    query: mutations.updatePremiumOrder,
                    variables: {
                        input: {
                            id,
                            active
                        }
                    }
                })
            ).data.updatePremiumOrder;
            if (response) return response;
        } catch (error) {
            console.error('Error updating premium order:', error);
            return null;
        }
    }

    async updatePremiumOrderSentToCoupa({
        id,
        sentToCoupa
    }: UpdatePremiumOrderInput) {
        try {
            const response = (
                await this.amplifyClient.graphql({
                    query: mutations.updatePremiumOrder,
                    variables: {
                        input: {
                            id,
                            sentToCoupa
                        }
                    }
                })
            ).data.updatePremiumOrder;
            if (response) return response;
        } catch (error) {
            console.error('Error updating premium order:', error);
            return null;
        }
    }

    async updateRequirement({
        id,
        requirement,
        created
    }: UpdatePremiumOrderInput) {
        try {
            const response = (
                await this.amplifyClient.graphql({
                    query: mutations.updatePremiumOrder,
                    variables: {
                        input: {
                            id,
                            requirement,
                            created
                        }
                    }
                })
            ).data.updatePremiumOrder;
            if (response) return response;
        } catch (error) {
            console.error('Error updating premium order:', error);
            return null;
        }
    }

    async updatePremiumOrderFiles({ id, files }: UpdatePremiumOrderInput) {
        try {
            const response = (
                await this.amplifyClient.graphql({
                    query: mutations.updatePremiumOrder,
                    variables: {
                        input: {
                            id,
                            files
                        }
                    }
                })
            ).data.updatePremiumOrder;
            if (response) return response;
        } catch (error) {
            console.error('Error updating premium order:', error);
            return null;
        }
    }

    async updatePremiumOrderBuyerInfo({
        id,
        buyerInfo,
        buyersInfo
    }: UpdatePremiumOrderInput) {
        try {
            const response = (
                await this.amplifyClient.graphql({
                    query: mutations.updatePremiumOrder,
                    variables: {
                        input: {
                            id,
                            buyerInfo,
                            buyersInfo
                        }
                    }
                })
            ).data.updatePremiumOrder;
            if (response) return response;
        } catch (error) {
            console.error('Error updating premium order:', error);
            return null;
        }
    }

    async updatePremiumOrderComments({
        id,
        comments
    }: UpdatePremiumOrderInput) {
        try {
            const response = (
                await this.amplifyClient.graphql({
                    query: mutations.updatePremiumOrder,
                    variables: {
                        input: {
                            id,
                            comments
                        }
                    }
                })
            ).data.updatePremiumOrder;
            if (response) return response;
        } catch (error) {
            console.error('Error updating premium order:', error);
            return null;
        }
    }

    async createPremiumOrder(body: CreatePremiumOrderInput) {
        try {
            const response = (
                await this.amplifyClient.graphql({
                    query: mutations.createPremiumOrder,
                    variables: {
                        input: body
                    }
                })
            ).data.createPremiumOrder;
            if (response) return response;
        } catch (error) {
            console.error('Error updating premium order:', error);
            return null;
        }
    }

    async updatePremiumOrderSteps({
        id,
        currentProcess,
        currentSubprocess,
        processes
    }: UpdatePremiumOrderInput) {
        try {
            const response = (
                await this.amplifyClient.graphql({
                    query: mutations.updatePremiumOrder,
                    variables: {
                        input: {
                            id,
                            currentProcess,
                            currentSubprocess,
                            processes
                        }
                    }
                })
            ).data.updatePremiumOrder;
            if (response) return response;
        } catch (error) {
            console.error('Error updating premium order:', error);
            return null;
        }
    }

    async deletePremiumOrder(id: string) {
        try {
            const response = (
                await this.amplifyClient.graphql({
                    query: mutations.deletePremiumOrder,
                    variables: {
                        input: {
                            id
                        }
                    }
                })
            ).data.deletePremiumOrder;
            if (response) return response;
        } catch (error) {
            console.error('Error deleting premium order:', error);
            return null;
        }
    }
}
